/**
 * Road identity: "which road am I on, which road am I turning onto".
 *
 * The driver asked for road NUMBERS on every sign, and for good reason - on
 * Thai highways the number is what's painted on the overhead gantry, while the
 * OSM name is a Thai string an English-speaking driver can't parse at 100 km/h.
 *
 * Driver-facing guidance deliberately uses ONLY these route numbers. Road and
 * street names remain on the raw route steps because the maneuver classifiers
 * use them, but they never reach the map card, route summaries, trip history,
 * or speech.
 */

import type { BannerContent, BannerInstruction, RouteStep } from '../core/types';
import type { Lang } from '../core/i18n';

/** A route number looks like 304, AH19, I-495 or L 1066. */
const REF_SHAPE = /^(?:[A-Z]{1,2}(?:\s|-)?\d{1,5}[A-Z]?|\d{1,5}[A-Z]?)$/i;

function splitRefs(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[;,/]/)
    .map((s) => s.trim())
    .filter((s) => REF_SHAPE.test(s));
}

function unique(refs: string[]): string[] {
  return refs.filter((ref, index) => refs.indexOf(ref) === index);
}

/** Route numbers carried by a step, most significant first. e.g. ['304','AH19'] */
export function stepRefs(step: RouteStep | null | undefined): string[] {
  if (!step) return [];
  const out = splitRefs(step.ref);
  if (out.length) return out;

  // Fall back to the shield icons in the step's own banner.
  for (const b of step.bannerInstructions ?? []) {
    const fromBanner = bannerRefs(b.primary).concat(bannerRefs(b.secondary));
    if (fromBanner.length) return fromBanner;
  }
  return splitRefs(step.destinations);
}

/** Route numbers a banner renders as shields. */
export function bannerRefs(content: BannerContent | undefined): string[] {
  if (!content?.components?.length) return [];
  const out: string[] = [];
  for (const c of content.components) {
    if (c.type !== 'icon') continue;
    const text = (c.text ?? '').trim();
    if (REF_SHAPE.test(text)) out.push(text);
  }
  return unique(out);
}

/**
 * Numbers safe to show or speak for this road.
 *
 * A selected approach banner may carry the shield for the road being joined
 * before Mapbox adds that number to the target step. We accept only its icon
 * components: exit numbers, sign text and destinations are not road numbers.
 * We intentionally do not inspect the target step's own future banners.
 */
export function roadNumberRefs(
  step: RouteStep | null | undefined,
  banner?: BannerInstruction | null,
): string[] {
  const direct = splitRefs(step?.ref);
  const signed = banner
    ? bannerRefs(banner.primary).concat(bannerRefs(banner.secondary))
    : [];
  return unique(direct.concat(signed));
}

/** Strictly recover route numbers from old persisted display labels. */
export function roadRefsFromStoredLabel(raw: string | null | undefined): string[] {
  return splitRefs(raw ?? '');
}

/** The single road number to badge, or null when the road is unnumbered. */
export function primaryRef(
  step: RouteStep | null | undefined,
  banner?: BannerInstruction | null,
): string | null {
  return roadNumberRefs(step, banner)[0] ?? null;
}

/**
 * Human label for the road a step travels along: route numbers only.
 */
export function roadLabel(
  step: RouteStep | null | undefined,
  _lang: Lang,
  banner?: BannerInstruction | null,
): string {
  return roadNumberRefs(step, banner).slice(0, 2).join(' / ');
}

/**
 * The road identity for a banner: shield numbers only, never its text.
 */
export function safeBannerText(
  banner: BannerInstruction | null,
  step: RouteStep | null,
  _lang: Lang,
): string {
  return roadLabel(step, _lang, banner);
}

/**
 * Label for the permanent road strip on the sign.
 *
 * The operator explicitly removed every street-name and road-class fallback.
 * An unnumbered road therefore returns an empty strip rather than a guess.
 */
export function roadStripParts(
  step: RouteStep | null | undefined,
  lang: Lang,
  banner?: BannerInstruction | null,
): { label: string; generic: boolean } {
  return { label: roadLabel(step, lang, banner), generic: false };
}

export function roadStripLabel(
  step: RouteStep | null | undefined,
  lang: Lang,
  banner?: BannerInstruction | null,
): string {
  return roadLabel(step, lang, banner);
}

/** True when the two steps run along different numbered roads. */
export function refChanges(from: RouteStep | null, to: RouteStep | null): boolean {
  const a = stepRefs(from);
  const b = stepRefs(to);
  if (!a.length && !b.length) return false;
  if (!a.length || !b.length) return true;
  // Sharing any number means we're still on the same road (304 -> 304/AH19).
  return !a.some((r) => b.includes(r));
}

/** True when the road NAME changes and both names are meaningful. */
export function nameChanges(from: RouteStep | null, to: RouteStep | null): boolean {
  const a = (from?.name ?? '').trim();
  const b = (to?.name ?? '').trim();
  if (!a || !b) return false;
  return a !== b;
}
